import { useMutation } from '@apollo/client';
import type { ApolloError } from '@apollo/client';
import type { FC } from 'react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@xing-com/button';
import type { FollowersFollowError } from '@xing-com/crate-common-graphql-types';
import {
  redirectToLogin,
  useLoggedinAction,
} from '@xing-com/crate-entity-pages-common';
import { trackEntityPageFollowAction } from '@xing-com/crate-entity-pages-common/src/tracking';
import { usePageContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-entity-page-context';
import { useErrorContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-error-context';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';

import { EntityPageFollowDocument } from '../../../graphql/mutations/entity-page-follow-mutation.gql-types';
import { EntityPageUnfollowDocument } from '../../../graphql/mutations/entity-page-unfollow-mutation.gql-types';

import { FollowActionConfirmation } from './follow-action-confirmation';

type Props = {
  onCompleted?: () => void;
  className?: string;
};
export const FollowButton: FC<Props> = ({ ...props }) => {
  const { isLoggedIn } = useLoginState();
  const { showError } = useErrorContext();
  const { pageContext, toggleFollowPage } = usePageContext();

  const isFollowingPage = !!pageContext.isFollowingPage;

  const [showFollowConfirmationDialog, setShowFollowConfirmationDialog] =
    useState(false);

  const [followPageMutation, { loading: followPageLoading }] = useMutation(
    EntityPageFollowDocument
  );

  const [unfollowPageMutation, { loading: unfollowPageLoading }] = useMutation(
    EntityPageUnfollowDocument
  );

  const handleError = (error: FollowersFollowError | ApolloError) => {
    trackEntityPageFollowAction({
      hasFollowed: isFollowingPage,
      error: true,
    });
    showError({ message: 'EP_JOBS_FEEDBACK_ERROR', error });
  };

  const toggleFollow = async (): Promise<void> => {
    if (!isLoggedIn) {
      redirectToLogin('followEntityPage');
      return;
    }

    try {
      if (isFollowingPage) {
        const { data, errors } = await unfollowPageMutation({
          variables: { followedId: pageContext.pageId },
        });

        const error = data?.entityPageUnfollow?.error || errors?.[0];
        if (error) {
          handleError(error);
        } else {
          trackEntityPageFollowAction({ hasFollowed: false });
          toggleFollowPage(false);
        }
      } else {
        const { data, errors } = await followPageMutation({
          variables: { followedId: pageContext.pageId },
        });

        const error = data?.entityPageFollow?.error || errors?.[0];
        if (error) {
          handleError(error);
        } else {
          trackEntityPageFollowAction({ hasFollowed: true });
          toggleFollowPage(true);
        }
      }
    } catch (error) {
      handleError(error as ApolloError);
    }
  };

  const toggleFollowWithDialog = () => {
    setTimeout(() => setShowFollowConfirmationDialog(true), 1000);
  };

  useLoggedinAction(toggleFollow, 'followEntityPage', isFollowingPage);
  useLoggedinAction(toggleFollowWithDialog, 'followConfirm', false);

  return (
    <>
      <FollowActionConfirmation
        show={showFollowConfirmationDialog}
        setShow={setShowFollowConfirmationDialog}
        onConfirm={toggleFollow}
      />
      <Button
        data-testid="followButton"
        variant={isFollowingPage ? 'tertiary' : 'primary'}
        size="medium"
        disabled={Boolean(followPageLoading || unfollowPageLoading)}
        loading={Boolean(followPageLoading || unfollowPageLoading)}
        onClick={(e) => {
          e.preventDefault();
          toggleFollow();
        }}
        {...props}
      >
        <FormattedMessage id={isFollowingPage ? 'EP_FOLLOWING' : 'EP_FOLLOW'} />
      </Button>
    </>
  );
};
